// ============================================================================
// Doc Order — Encompass pull parsing + the facts-only refresh merge.
// The edge function fetches fieldReader batches; THIS module owns turning the
// raw field map into snapshot/fee rows and merging refreshes. The merge rule
// is the most bug-prone seam in the feature (plan risk #3) — keep it here,
// pure and exhaustively tested; never inline it in the edge function.
//
// Fail-closed rule (Codex-2 R3): getFieldValues returns {} on HTTP failure,
// so a pull missing REQUIRED fields is a FAILED pull — the caller must 502
// and materialize nothing.
// ============================================================================

import { DO_FEE_LINE_FIELDS, DO_LOAN_FIELDS, type FeeLineId } from './field-map.ts';
import { normalizeIncomingFee } from './fees.ts';
import type { DocOrderFee, FeeTiming, PaidBy } from './types.ts';

export type FieldValues = Record<string, string>;

/** Loan-level pull list — REAL field IDs only (TBD/null/prose + wizard-owned marital/sex excluded). */
export const LOAN_PULL_FIELDS: string[] = [
  String(DO_LOAN_FIELDS.totalLoanAmount),
  String(DO_LOAN_FIELDS.baseLoanAmount),
  String(DO_LOAN_FIELDS.vestingDescription),
  String(DO_LOAN_FIELDS.subjectPropertyState),
  String(DO_LOAN_FIELDS.lenderCredit),
  String(DO_LOAN_FIELDS.cashToFromBorrower),
  String(DO_LOAN_FIELDS.llcEntityName),
  String(DO_LOAN_FIELDS.llcEntityState),
  String(DO_LOAN_FIELDS.llcEntityOrgType),
  String(DO_LOAN_FIELDS.llcEntityEIN),
  String(DO_LOAN_FIELDS.settlementAgent),
  String(DO_LOAN_FIELDS.settlementAgentName),
  String(DO_LOAN_FIELDS.settlementAgentPhone),
  String(DO_LOAN_FIELDS.settlementAgentEmail),
  String(DO_LOAN_FIELDS.hazardInsuranceCompany),
  String(DO_LOAN_FIELDS.hazardMonthlyPremium),
  String(DO_LOAN_FIELDS.hazardAgentPhone),
  String(DO_LOAN_FIELDS.hazardAgentEmail),
  String(DO_LOAN_FIELDS.initialCdSentDate),
  String(DO_LOAN_FIELDS.initialGfeSentDate),
  String(DO_LOAN_FIELDS.piwIndicator),
  String(DO_LOAN_FIELDS.dicCompany),
  String(DO_LOAN_FIELDS.dicAgentName),
  String(DO_LOAN_FIELDS.dicMonthlyPremium),
  String(DO_LOAN_FIELDS.dicPhone),
  String(DO_LOAN_FIELDS.dicEmail),
  String(DO_LOAN_FIELDS.loanChannel),
];

/** Pull failure ⇒ create/refresh must fail closed when any of these is absent. */
export const REQUIRED_PULL_FIELDS: string[] = [
  String(DO_LOAN_FIELDS.totalLoanAmount),
  String(DO_LOAN_FIELDS.loanChannel),
  String(DO_LOAN_FIELDS.subjectPropertyState),
];

/**
 * Doc-order fee lines: third-party §800 lines only (803–818). 801a/802e are
 * lender-side (origination/discount — P&P/LE owned) and never render in the
 * Third-Party Fees table; they stay in the field map for write-back
 * completeness only. Ops #15: 804 is ALWAYS Appraisal 1.
 */
export const DOC_ORDER_FEE_LINES: FeeLineId[] = [
  '803', '804', '805', '806', '807', '808', '809', '810',
  '811', '812', '813', '814', '815', '816', '817', '818',
];

/** Hard-coded Encompass names for the fixed lines (no feeDescription field). */
const FIXED_LINE_NAMES: Partial<Record<FeeLineId, string>> = {
  '804': 'Appraisal Fee',
  '805': 'Credit Report',
  '806': 'Tax Service Fee',
  '807': 'Flood Certification',
};

/** All grid field IDs needed for the doc-order fee pull (chunk before fetching). */
export function feeGridPullFields(): string[] {
  const ids = new Set<string>();
  for (const line of DOC_ORDER_FEE_LINES) {
    const f = DO_FEE_LINE_FIELDS[line];
    for (const key of [
      'feeDescription', 'payee', 'paidToName', 'totalFee', 'lastDisclosedLE', 'lastDisclosedCD',
      'borrowerPAC', 'borrowerPOC', 'borrowerTotal',
      'sellerPAC', 'sellerPOC', 'sellerTotal',
      'brokerPAC', 'brokerPOC', 'brokerTotal',
      'lenderPAC', 'lenderPOC', 'lenderTotal',
      'otherPAC', 'otherPOC', 'otherTotal',
    ] as const) {
      const id = f[key];
      if (id !== undefined && id !== null) ids.add(String(id));
    }
  }
  return Array.from(ids);
}

export function chunkFieldIds(ids: string[], size = 250): string[][] {
  const out: string[][] = [];
  for (let i = 0; i < ids.length; i += size) out.push(ids.slice(i, i + size));
  return out;
}

/** '$1,234.56' / '1234.56' / '' → number|null. */
export function moneyToNumber(raw: string | undefined | null): number | null {
  if (raw === undefined || raw === null) return null;
  const cleaned = String(raw).replace(/[$,\s]/g, '');
  if (cleaned === '') return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

export function boolFromField(raw: string | undefined | null): boolean {
  return /^(y|yes|true|1|x)$/i.test(String(raw ?? '').trim());
}

export function missingRequiredFields(values: FieldValues): string[] {
  return REQUIRED_PULL_FIELDS.filter((id) => {
    const v = values[id];
    return v === undefined || v === null || String(v).trim() === '';
  });
}

export interface PulledFeeFacts {
  line: FeeLineId;
  name: string;
  payee: string | null;
  amount: number;
  lastDisclosedLE: number | null;
  lastDisclosedCD: number | null;
  encPaidBy: string | null;
  /** Incoming disposition read off the payer×timing grid (pre-normalization). */
  incomingPaidBy: PaidBy | null;
  incomingTiming: FeeTiming | null;
}

const PAYER_BUCKETS = [
  ['Borrower', 'borrowerPAC', 'borrowerPOC', 'borrowerTotal'],
  ['Seller', 'sellerPAC', 'sellerPOC', 'sellerTotal'],
  ['Broker', 'brokerPAC', 'brokerPOC', 'brokerTotal'],
  ['Lender', 'lenderPAC', 'lenderPOC', 'lenderTotal'],
  ['Other', 'otherPAC', 'otherPOC', 'otherTotal'],
] as const;

/**
 * Extract charged fee lines from the grid values. A line materializes only
 * when its total charge > 0 ($0 lines are never shown — README §5).
 * `encPaidBy` = the dominant populated payer bucket (informational, ops #12).
 * The incoming disposition maps: dominant-bucket POC-only → POC (Borrower vs
 * lender-side); anything else → PAC (which normalization then CLEARS).
 */
export function extractFeeLines(values: FieldValues): PulledFeeFacts[] {
  const out: PulledFeeFacts[] = [];
  for (const line of DOC_ORDER_FEE_LINES) {
    const f = DO_FEE_LINE_FIELDS[line];
    const get = (id: string | number | undefined | null) =>
      id === undefined || id === null ? undefined : values[String(id)];
    const amount = moneyToNumber(get(f.totalFee));
    if (!amount || amount <= 0) continue;

    let name = FIXED_LINE_NAMES[line] ?? (get(f.feeDescription) || '').trim();
    if (!name) name = `Line ${line} Fee`;
    const payee = (get(f.payee) ?? get(f.paidToName) ?? '').trim() || null;

    let encPaidBy: string | null = null;
    let dominantTotal = 0;
    let pocOnly = false;
    for (const [label, pacKey, pocKey, totalKey] of PAYER_BUCKETS) {
      const total = moneyToNumber(get(f[totalKey])) ?? 0;
      if (total > dominantTotal) {
        dominantTotal = total;
        encPaidBy = label;
        const pac = moneyToNumber(get(f[pacKey])) ?? 0;
        const poc = moneyToNumber(get(f[pocKey])) ?? 0;
        pocOnly = poc > 0 && pac <= 0;
      }
    }

    const incomingTiming: FeeTiming | null = encPaidBy ? (pocOnly ? 'POC' : 'PAC') : null;
    const incomingPaidBy: PaidBy | null =
      encPaidBy === 'Borrower' ? 'Borrower' : encPaidBy ? 'Lendwise' : null;

    out.push({
      line,
      name,
      payee,
      amount,
      lastDisclosedLE: moneyToNumber(get(f.lastDisclosedLE)),
      lastDisclosedCD: moneyToNumber(get(f.lastDisclosedCD)),
      encPaidBy,
      incomingPaidBy,
      incomingTiming,
    });
  }
  return out;
}

/** Build a FIRST-MATERIALIZATION fee row: Encompass facts + normalized (unset) disposition. */
export function materializeFee(facts: PulledFeeFacts, displayOrder: number): DocOrderFee {
  const normalized = normalizeIncomingFee({
    amount: facts.amount,
    paidBy: facts.incomingPaidBy,
    timing: facts.incomingTiming,
    reimburse: false,
  });
  const isCreditReport = facts.line === '805';
  return {
    id: `fee-${facts.line}`,
    feeKey: facts.line,
    line: facts.line,
    name: facts.name,
    payee: facts.payee,
    source: 'encompass',
    amount: facts.amount,
    paidBy: normalized.paidBy,
    timing: normalized.timing,
    reimburse: normalized.reimburse,
    notApplicable: false,
    confirmed: false,
    ...(isCreditReport ? { refreshCredit: null } : {}),
    splits: null,
    reconciled: false,
    acks: {},
    encompassAmount: facts.amount,
    lastDisclosedLE: facts.lastDisclosedLE,
    lastDisclosedCD: facts.lastDisclosedCD,
    encPaidBy: facts.encPaidBy,
    invoices: [],
    approvedBy: null,
    approvedAt: null,
    displayOrder,
  };
}

/**
 * Facts-only refresh merge: updates Encompass-fact surfaces on an EXISTING fee
 * row and NOTHING else. Decision columns (amount, paidBy, timing, reimburse,
 * splits, refreshCredit, notApplicable, reconciled, acks, approvals, invoices)
 * are never touched — and NEITHER are name/payee: the prototype makes fee
 * names/payees processor-editable, so a refresh must not clobber them (Codex
 * P1-review finding 4). A line zeroed in Encompass keeps its row — the diff
 * surfaces in Closing's EncompassDiffSheet instead (plan §refresh semantics).
 */
export function refreshMergeFee(existing: DocOrderFee, facts: PulledFeeFacts): DocOrderFee {
  return {
    ...existing,
    encompassAmount: facts.amount,
    lastDisclosedLE: facts.lastDisclosedLE,
    lastDisclosedCD: facts.lastDisclosedCD,
    encPaidBy: facts.encPaidBy,
  };
}

/**
 * Full refresh reconciliation: merge facts into existing rows by feeKey,
 * MATERIALIZE (normalize) rows for lines that newly appeared, and zero the
 * encompassAmount on rows whose line vanished from the pull (keeps the row).
 */
export function reconcileFeesOnRefresh(
  existing: DocOrderFee[],
  pulled: PulledFeeFacts[]
): DocOrderFee[] {
  const byKey = new Map(existing.map((f) => [f.feeKey, f]));
  const seen = new Set<string>();
  const out: DocOrderFee[] = [];
  let nextOrder = existing.reduce((m, f) => Math.max(m, f.displayOrder), 0);

  for (const f of existing) {
    const facts = pulled.find((p) => p.line === f.feeKey);
    if (facts) {
      seen.add(facts.line);
      out.push(refreshMergeFee(f, facts));
    } else if (f.source === 'encompass') {
      out.push({ ...f, encompassAmount: 0 });
    } else {
      out.push(f);
    }
  }
  for (const facts of pulled) {
    if (seen.has(facts.line) || byKey.has(facts.line)) continue;
    out.push(materializeFee(facts, ++nextOrder));
  }
  return out;
}

/**
 * Full subject-property address on ONE line (R4 #14 — lifted from the client
 * viewModel so the server PDF/derive and the web Snapshot share ONE
 * implementation): street + city + state + ZIP, skipping empty or "TBD"
 * placeholder parts so a not-yet-set street never renders stray punctuation.
 */
export function assemblePropertyAddress(parts: {
  street?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
}): string | null {
  const clean = (v: string | null | undefined): string => {
    const s = String(v ?? '').trim();
    return s && s.toUpperCase() !== 'TBD' ? s : '';
  };
  const stateZip = [clean(parts.state), clean(parts.zip)].filter(Boolean).join(' ');
  const full = [clean(parts.street), clean(parts.city), stateZip].filter(Boolean).join(', ');
  return full || null;
}
